'use client';

/**
 * useModelPreference — the user's default chat model (per-user, persisted).
 *
 * Loads `GET {apiBaseUrl}/model-preference` (supported-model catalog + the
 * caller's selection + the env-resolved system default) and saves via
 * `PUT {apiBaseUrl}/model-preference`. Multiple mounted instances (settings
 * modal, start-screen chip) stay in sync through a window event fired after
 * every successful save.
 *
 * The catalog is SERVER-DRIVEN — this hook never hardcodes model ids; the
 * registry in `lib/models/registry` (served by the hub route) is the single
 * source of truth for what can be picked.
 */

import { useCallback, useEffect, useState } from 'react';
import { getAuthToken } from '../lib/api/authenticated-fetch';

/** Mirror of `MeridianModelOption` as served by the model-preference route. */
export interface ModelPreferenceOption {
  id: string;
  label: string;
  provider: 'aws' | 'deepseek';
  description: string;
  toolSearchCapable: boolean;
}

export interface ModelPreferenceSystemDefault {
  modelId: string;
  provider: 'aws' | 'deepseek';
  label: string;
}

interface ModelPreferenceResponse {
  models?: ModelPreferenceOption[];
  selectedModelId?: string | null;
  systemDefault?: ModelPreferenceSystemDefault;
}

/** Cross-instance sync signal (settings modal save → start-screen chip). */
const MODEL_PREFERENCE_CHANGED_EVENT = 'meridian:model-preference-changed';

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface UseModelPreferenceOptions {
  apiBaseUrl?: string;
  /** Skip fetching entirely (e.g. surfaces without Meridian API access). */
  enabled?: boolean;
}

export function useModelPreference(options: UseModelPreferenceOptions = {}) {
  const { apiBaseUrl = '/api/meridian', enabled = true } = options;
  const [models, setModels] = useState<ModelPreferenceOption[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [systemDefault, setSystemDefault] = useState<ModelPreferenceSystemDefault | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/model-preference`, {
        headers: buildHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load model settings');
      const data = (await res.json()) as ModelPreferenceResponse;
      setModels(Array.isArray(data.models) ? data.models : []);
      setSelectedModelId(typeof data.selectedModelId === 'string' ? data.selectedModelId : null);
      setSystemDefault(data.systemDefault ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load model settings');
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  // Refetch when another instance saves (modal → chip sync).
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onChanged = () => {
      load();
    };
    window.addEventListener(MODEL_PREFERENCE_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(MODEL_PREFERENCE_CHANGED_EVENT, onChanged);
  }, [enabled, load]);

  /** Persist a model id (or `null` to clear back to the system default). */
  const saveModelId = useCallback(
    async (modelId: string | null): Promise<boolean> => {
      setIsSaving(true);
      setError(null);
      try {
        const res = await fetch(`${apiBaseUrl}/model-preference`, {
          method: 'PUT',
          headers: buildHeaders(),
          credentials: 'include',
          body: JSON.stringify({ modelId }),
        });
        if (!res.ok) throw new Error('Failed to save model preference');
        const data = (await res.json()) as ModelPreferenceResponse;
        setSelectedModelId(typeof data.selectedModelId === 'string' ? data.selectedModelId : null);
        if (data.systemDefault) setSystemDefault(data.systemDefault);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(MODEL_PREFERENCE_CHANGED_EVENT));
        }
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save model preference');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBaseUrl]
  );

  const selectedModel = models.find((m) => m.id === selectedModelId) ?? null;
  /** Chip label: the user's pick, else the system default's label. */
  const activeModelLabel = selectedModel?.label ?? systemDefault?.label;

  return {
    models,
    selectedModelId,
    selectedModel,
    systemDefault,
    activeModelLabel,
    isLoading,
    isSaving,
    error,
    reload: load,
    saveModelId,
  };
}
